import {
  type BirthdayMembersDto,
  type BoxDashboardKpiDto,
  type CommitedClubDto,
  type DailyScheduleListDto,
  type DashboardFinalRoundDto,
  type DashboardRookieMemberDto,
  type FinalRoundType,
  type InactiveMemberDto,
  type RookieMilestone,
  type SlotParticipantLightDto,
  type TeamTaskDto,
  type TotalFinalRoundDto,
  type TotalRookiesDto,
  type TrialsDropInsBirthdaysDto,
  type UpsertTeamTaskDto,
  type WeeklyTrialsDropInsBirthdaysDto,
} from "@/modules/boxDashboard/gateway/boxDashboardApiDto"
import type { BoxDashboardApiGateway } from "@/modules/boxDashboard/gateway/boxDashboardApiGateway"
import {
  COMMITED_CLUB_MEMBER_DEFAULT,
  COMMITED_CLUB_MEMBERS_LIST,
} from "@/modules/boxDashboard/modules/CommitedClub/tests/fakeData/commitedClubMembers"
import {
  BIRTHDAY_MEMBER_DEFAULT,
  DAILY_SCHEDULE_LIST,
  DAILY_SCHEDULE_SLOT_DEFAULT,
  SLOT_PARTICIPANT_DEFAULT,
  TRIALS_DROPINS_DAY_DEFAULT,
} from "@/modules/boxDashboard/modules/DailySchedule/tests/fakeData/dailySchedule"
import {
  FINAL_ROUND_MEMBER_DEFAULT,
  FINAL_ROUND_MEMBERS_LIST,
} from "@/modules/boxDashboard/modules/FinalRoundMembers/tests/fakeData/finalRoundMembers"
import {
  INACTIVE_MEMBER_DEFAULT,
  INACTIVE_MEMBERS_LIST,
} from "@/modules/boxDashboard/modules/InactiveMembers/tests/fakeData/inactiveMembers"
import { KPI_INCOME_WITH_COMPARISON } from "@/modules/boxDashboard/modules/KPI/tests/fakeData/boxDashboardKpi"
import {
  ROOKIE_MEMBER_DEFAULT,
  ROOKIE_MEMBERS_LIST,
} from "@/modules/boxDashboard/modules/RookieMembers/tests/fakeData/rookieMembers"
import { BOX_STAFF_TEST_DATA } from "@modules/boxStaff/gateway/boxStaffApi.fake"
import type { Paginated } from "@modules/utils/paginated"
import dayjs from "dayjs"
import { v4 as uuidv4 } from "uuid"

// ============================================================================
// Test Data Constants
// ============================================================================

const _TEAM_TASK_DEFAULT: TeamTaskDto = {
  id: "task-default",
  task: "Default task content",
  taskStatus: "CREATED",
  creator: BOX_STAFF_TEST_DATA.OWNER,
  updater: null,
  createdDate: dayjs().toISOString(),
  updatedDate: null,
  completedDate: null,
  archivedDate: null,
  archived: false,
}

export const BOX_DASHBOARD_TEST_DATA: {
  DAILY_SCHEDULE_SLOT: { DEFAULT: typeof DAILY_SCHEDULE_SLOT_DEFAULT; LIST: typeof DAILY_SCHEDULE_LIST }
  TEAM_TASK: { DEFAULT: TeamTaskDto }
  SLOT_PARTICIPANT: { DEFAULT: typeof SLOT_PARTICIPANT_DEFAULT }
  BIRTHDAY_MEMBER: { DEFAULT: typeof BIRTHDAY_MEMBER_DEFAULT }
  TRIALS_DROPINS_DAY: { DEFAULT: typeof TRIALS_DROPINS_DAY_DEFAULT }
  INACTIVE_MEMBER: { DEFAULT: typeof INACTIVE_MEMBER_DEFAULT; LIST: typeof INACTIVE_MEMBERS_LIST }
  ROOKIE_MEMBER: { DEFAULT: typeof ROOKIE_MEMBER_DEFAULT; LIST: typeof ROOKIE_MEMBERS_LIST }
  FINAL_ROUND_MEMBER: { DEFAULT: typeof FINAL_ROUND_MEMBER_DEFAULT; LIST: typeof FINAL_ROUND_MEMBERS_LIST }
  MOST_ENGAGED: { DEFAULT: typeof COMMITED_CLUB_MEMBER_DEFAULT; LIST: typeof COMMITED_CLUB_MEMBERS_LIST }
} = {
  DAILY_SCHEDULE_SLOT: {
    DEFAULT: DAILY_SCHEDULE_SLOT_DEFAULT,
    LIST: DAILY_SCHEDULE_LIST,
  },
  TEAM_TASK: {
    DEFAULT: _TEAM_TASK_DEFAULT,
  },
  SLOT_PARTICIPANT: {
    DEFAULT: SLOT_PARTICIPANT_DEFAULT,
  },
  BIRTHDAY_MEMBER: {
    DEFAULT: BIRTHDAY_MEMBER_DEFAULT,
  },
  TRIALS_DROPINS_DAY: {
    DEFAULT: TRIALS_DROPINS_DAY_DEFAULT,
  },
  INACTIVE_MEMBER: {
    DEFAULT: INACTIVE_MEMBER_DEFAULT,
    LIST: INACTIVE_MEMBERS_LIST,
  },
  ROOKIE_MEMBER: {
    DEFAULT: ROOKIE_MEMBER_DEFAULT,
    LIST: ROOKIE_MEMBERS_LIST,
  },
  FINAL_ROUND_MEMBER: {
    DEFAULT: FINAL_ROUND_MEMBER_DEFAULT,
    LIST: FINAL_ROUND_MEMBERS_LIST,
  },
  MOST_ENGAGED: {
    DEFAULT: COMMITED_CLUB_MEMBER_DEFAULT,
    LIST: COMMITED_CLUB_MEMBERS_LIST,
  },
}

// ============================================================================
// Fake API Implementation
// ============================================================================

export class FakeBoxDashboardApi implements BoxDashboardApiGateway {
  private dailySchedule: DailyScheduleListDto = [...DAILY_SCHEDULE_LIST]
  private activeTasks: TeamTaskDto[] = []
  private completedTasks: TeamTaskDto[] = []
  private inactiveMembers: InactiveMemberDto[] = [...INACTIVE_MEMBERS_LIST]
  private rookieMembers: DashboardRookieMemberDto[] = [...ROOKIE_MEMBERS_LIST]
  private finalRoundMembers: DashboardFinalRoundDto[] = [...FINAL_ROUND_MEMBERS_LIST]
  private commitedClubMembers: CommitedClubDto[] = [...COMMITED_CLUB_MEMBERS_LIST]
  private kpi: BoxDashboardKpiDto = { ...KPI_INCOME_WITH_COMPARISON }

  // ========== Setup Methods ==========

  givenDailySchedule(schedule: DailyScheduleListDto) {
    this.dailySchedule = schedule
  }

  givenActiveTasks(tasks: TeamTaskDto[]) {
    this.activeTasks = tasks
  }

  givenCompletedTasks(tasks: TeamTaskDto[]) {
    this.completedTasks = tasks
  }

  givenInactiveMembers(members: InactiveMemberDto[]) {
    this.inactiveMembers = members
  }

  // ========== API Methods ==========

  getDailySchedule(): Promise<DailyScheduleListDto> {
    return Promise.resolve([...this.dailySchedule])
  }

  getTrialsDropInsBirthdays(startDate: string, endDate: string): Promise<WeeklyTrialsDropInsBirthdaysDto> {
    const start = dayjs(startDate)
    const end = dayjs(endDate)
    const days = end.diff(start, "day") + 1

    // Generate fake trial/drop-in participants for testing
    const generateTrialParticipants = (
      date: string,
      count: number,
      withAbsent?: boolean,
    ): SlotParticipantLightDto[] => {
      const names = [
        { first: "William", last: "Smith" },
        { first: "Cyril", last: "Vincent" },
        { first: "Elena", last: "Torres" },
        { first: "Sofia", last: "Lopez" },
        { first: "James", last: "Rodriguez" },
        { first: "Liam", last: "Parker" },
        { first: "Ava", last: "Nelson" },
        { first: "Isabella", last: "Scott" },
        { first: "Charlotte", last: "Hill" },
        { first: "Amelia", last: "Johnson" },
      ]
      const programs = ["WOD", "Marathon Crossfit", "Yoga Studio", "Pilates Center", "Zumba Class", "Kickboxing Gym"]
      return Array.from({ length: count }, (_, i) => {
        const name = names[i % names.length] ?? { first: "Guest", last: "User" }
        const program = programs[i % programs.length] ?? "Program"

        const slotStartDate = dayjs(date)
          .hour(9 + i)
          .minute(0)
          .toISOString()

        const isAbsent = withAbsent ? i % 2 === 0 : false

        return {
          boxMemberId: `member-trial-${date}-${i}`,
          username: `${name.first.toLowerCase()}${name.last.toLowerCase()}`,
          firstname: name.first,
          lastname: name.last,
          slotStart: isAbsent ? null : slotStartDate,
          programName: program,
          assignedStaffMembers: null,
        }
      })
    }

    const generateBirthdayParticipants = (date: string, count: number, withAbsent?: boolean): BirthdayMembersDto[] => {
      const names = [
        { first: "William", last: "Smith" },
        { first: "Cyril", last: "Vincent" },
        { first: "Elena", last: "Torres" },
        { first: "Sofia", last: "Lopez" },
        { first: "James", last: "Rodriguez" },
        { first: "Liam", last: "Parker" },
        { first: "Ava", last: "Nelson" },
        { first: "Isabella", last: "Scott" },
        { first: "Charlotte", last: "Hill" },
        { first: "Amelia", last: "Johnson" },
      ]
      const programs = ["WOD", "Marathon Crossfit", "Yoga Studio", "Pilates Center", "Zumba Class", "Kickboxing Gym"]
      return Array.from({ length: count }, (_, i) => {
        const name = names[i % names.length] ?? { first: "Guest", last: "User" }
        const program = programs[i % programs.length] ?? "Program"

        const slotStartDate = dayjs(date)
          .hour(9 + i)
          .minute(0)
          .toISOString()

        const isAbsent = withAbsent ? i % 2 === 0 : false

        return {
          boxMemberId: `member-trial-${date}-${i}`,
          username: `${name.first.toLowerCase()}${name.last.toLowerCase()}`,
          firstname: name.first,
          lastname: name.last,
          slotStart: isAbsent ? null : slotStartDate,
          programName: program,
          assignedStaffMembers: null,
          lastSessionDate: dayjs(date).subtract(4, "day").toISOString(),
          nextSessionDate: dayjs(date).add(3, "day").toISOString(),
        }
      })
    }

    // Generate days with some having trials/drop-ins for testing
    const today = dayjs().format("YYYY-MM-DD")
    const weekData: TrialsDropInsBirthdaysDto[] = Array.from({ length: days }, (_, i) => {
      const dayDate = start.add(i, "days").format("YYYY-MM-DD")
      const isToday = dayDate === today
      const hasTrials = isToday || i === 1 || i === 3 // Today, Tuesday and Thursday have trials
      const hasDropIns = isToday || i === 2 || i === 4 // Today, Wednesday and Friday have drop-ins
      const hasBirthdays = isToday // Today has birthdays
      const trialCount = hasTrials ? 8 : 0 // 8 trials to test scroll
      const dropInCount = hasDropIns ? 5 : 0
      const birthdayCount = hasBirthdays ? 3 : 0

      return {
        dayDate,
        numberOfTrials: trialCount,
        numberOfDropIns: dropInCount,
        numberOfBirthdays: birthdayCount,
        trials: hasTrials ? generateTrialParticipants(dayDate, trialCount) : null,
        dropIns: hasDropIns ? generateTrialParticipants(dayDate, dropInCount) : null,
        birthdays: hasBirthdays ? generateBirthdayParticipants(dayDate, birthdayCount, true) : null,
      }
    })

    return Promise.resolve(weekData)
  }

  async getActiveTasks(page = 0, pageSize = 20): Promise<Paginated<TeamTaskDto>> {
    const start = page * pageSize
    const end = start + pageSize
    const paginatedTasks = this.activeTasks.slice(start, end)

    return Promise.resolve({
      content: paginatedTasks,
      contentSize: paginatedTasks.length,
      offset: start,
      page,
      pageSize,
      totalElements: this.activeTasks.length,
      totalPages: Math.ceil(this.activeTasks.length / pageSize),
      last: end >= this.activeTasks.length,
    })
  }

  async getCompletedTasks(page = 0, pageSize = 20): Promise<Paginated<TeamTaskDto>> {
    const start = page * pageSize
    const end = start + pageSize
    const paginatedTasks = this.completedTasks.slice(start, end)

    return Promise.resolve({
      content: paginatedTasks,
      contentSize: paginatedTasks.length,
      offset: start,
      page,
      pageSize,
      totalElements: this.completedTasks.length,
      totalPages: Math.ceil(this.completedTasks.length / pageSize),
      last: end >= this.completedTasks.length,
    })
  }

  createTeamTask(data: UpsertTeamTaskDto): Promise<TeamTaskDto> {
    const newTask: TeamTaskDto = {
      id: uuidv4(),
      task: data.task ?? "New task",
      taskStatus: data.taskStatus ?? "CREATED",
      creator: BOX_STAFF_TEST_DATA.OWNER,
      updater: null,
      createdDate: dayjs().toISOString(),
      updatedDate: null,
      completedDate: null,
      archivedDate: null,
      archived: false,
    }

    this.activeTasks.push(newTask)

    return Promise.resolve(newTask)
  }

  updateTeamTask(id: string, data: UpsertTeamTaskDto): Promise<TeamTaskDto> {
    // Find task in active or completed lists
    let task = this.activeTasks.find((t) => t.id === id)
    let isActive = true

    if (!task) {
      task = this.completedTasks.find((t) => t.id === id)
      isActive = false
    }

    if (!task) {
      throw new Error(`Task with id ${id} not found`)
    }

    const updatedTask: TeamTaskDto = {
      ...task,
      task: data.task ?? task.task,
      taskStatus: data.taskStatus ?? task.taskStatus,
      updater: BOX_STAFF_TEST_DATA.OWNER,
      updatedDate: dayjs().toISOString(),
    }

    if (isActive) {
      const index = this.activeTasks.findIndex((t) => t.id === id)
      this.activeTasks[index] = updatedTask
    } else {
      const index = this.completedTasks.findIndex((t) => t.id === id)
      this.completedTasks[index] = updatedTask
    }

    return Promise.resolve(updatedTask)
  }

  deleteTeamTask(id: string): Promise<void> {
    this.activeTasks = this.activeTasks.filter((t) => t.id !== id)
    return Promise.resolve()
  }

  completeTeamTask(id: string): Promise<TeamTaskDto> {
    const task = this.activeTasks.find((t) => t.id === id)

    if (!task) {
      throw new Error(`Task with id ${id} not found in active tasks`)
    }

    const completedTask: TeamTaskDto = {
      ...task,
      taskStatus: "COMPLETED",
      completedDate: dayjs().toISOString(),
      updater: BOX_STAFF_TEST_DATA.OWNER,
      updatedDate: dayjs().toISOString(),
    }

    this.activeTasks = this.activeTasks.map((t) => (t.id === id ? completedTask : t))

    return Promise.resolve(completedTask)
  }

  async getInactiveMembers(page = 0, pageSize = 10): Promise<Paginated<InactiveMemberDto>> {
    const start = page * pageSize
    const end = start + pageSize
    const paginatedMembers = this.inactiveMembers.slice(start, end)

    return Promise.resolve({
      content: paginatedMembers,
      contentSize: paginatedMembers.length,
      offset: start,
      page,
      pageSize,
      totalElements: this.inactiveMembers.length,
      totalPages: Math.ceil(this.inactiveMembers.length / pageSize),
      last: end >= this.inactiveMembers.length,
    })
  }

  async archiveInactiveMember(id: string): Promise<void> {
    this.inactiveMembers = this.inactiveMembers.filter((m) => m.boxMemberItemDto.id !== id)
    return Promise.resolve()
  }

  async getTotalRookies(): Promise<TotalRookiesDto> {
    return Promise.resolve({ total: this.rookieMembers.length })
  }

  async getRookieMembers(
    milestone?: RookieMilestone,
    page = 0,
    pageSize = 10,
  ): Promise<Paginated<DashboardRookieMemberDto>> {
    let filteredMembers = this.rookieMembers

    if (milestone) {
      filteredMembers = this.rookieMembers.filter((m) => {
        const days = m.nbOfDaysSinceRegistration
        switch (milestone) {
          case "UNDER_15_DAYS":
            return days < 15
          case "UNDER_30_DAYS":
            return days >= 15 && days < 30
          case "UNDER_45_DAYS":
            return days >= 30 && days < 45
          case "UNDER_60_DAYS":
            return days >= 45 && days < 60
          default:
            return true
        }
      })
    }

    const start = page * pageSize
    const end = start + pageSize
    const paginatedMembers = filteredMembers.slice(start, end)

    return Promise.resolve({
      content: paginatedMembers,
      contentSize: paginatedMembers.length,
      offset: start,
      page,
      pageSize,
      totalElements: filteredMembers.length,
      totalPages: Math.ceil(filteredMembers.length / pageSize),
      last: end >= filteredMembers.length,
    })
  }

  givenRookieMembers(members: DashboardRookieMemberDto[]) {
    this.rookieMembers = members
  }

  givenFinalRoundMembers(members: DashboardFinalRoundDto[]) {
    this.finalRoundMembers = members
  }

  async getTotalFinalRound(): Promise<TotalFinalRoundDto> {
    const classPassTotal = this.finalRoundMembers.filter((m) => m.remainingCount !== null).length
    const membershipTotal = this.finalRoundMembers.filter((m) => m.remainingCount === null).length
    return Promise.resolve({
      total: this.finalRoundMembers.length,
      classPassTotal,
      membershipTotal,
    })
  }

  async getFinalRoundMembers(
    offerType?: FinalRoundType,
    page = 0,
    pageSize = 10,
  ): Promise<Paginated<DashboardFinalRoundDto>> {
    let filteredMembers = this.finalRoundMembers

    if (offerType === "CLASS_PASS") {
      filteredMembers = this.finalRoundMembers.filter((m) => m.remainingCount !== null)
    } else if (offerType === "MEMBERSHIP") {
      filteredMembers = this.finalRoundMembers.filter((m) => m.remainingCount === null)
    }

    // Sort by end date (most critical first)
    filteredMembers = [...filteredMembers].sort(
      (a, b) => new Date(a.endDate ?? "").getTime() - new Date(b.endDate ?? "").getTime(),
    )

    const start = page * pageSize
    const end = start + pageSize
    const paginatedMembers = filteredMembers.slice(start, end)

    return Promise.resolve({
      content: paginatedMembers,
      contentSize: paginatedMembers.length,
      offset: start,
      page,
      pageSize,
      totalElements: filteredMembers.length,
      totalPages: Math.ceil(filteredMembers.length / pageSize),
      last: end >= filteredMembers.length,
    })
  }

  async getCommitedClub(_: {
    periodType: "MONTH" | "YEAR"
    year: number
    month?: number
  }): Promise<Paginated<CommitedClubDto>> {
    return Promise.resolve({
      content: [...this.commitedClubMembers],
      contentSize: this.commitedClubMembers.length,
      offset: 0,
      page: 0,
      pageSize: 10,
      totalElements: this.commitedClubMembers.length,
      totalPages: 1,
      last: true,
    })
  }

  async getKPI(): Promise<BoxDashboardKpiDto> {
    return Promise.resolve(this.kpi)
  }
}
